import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";

import ImagePicker from "../../../shared/components/ImagePicker";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";

import { useUpdateCurrentTenant, useUpdateTenantLogo } from "../hooks/useTenantMutations";
import Logo from "./Logo";

const useStyles = makeStyles({
  root: {
    overflow: "visible",
    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },
  body: {
    overflowX: "hidden",
  },
  content: {
    flex: "1",
    width: "100%",
    minWidth: 0,
    padding: "16px 0",
    boxSizing: "border-box",

    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "stretch",
    // gridRowGap: tokens.spacingVerticalS,
    gridAutoRows: "max-content",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  logo: {
    width: "64px",
    height: "64px",
    objectFit: "cover",
    backgroundColor: "transparent",
  },
  placeholderLogo: {
    width: "64px",
    height: "64px",
    display: "grid",
    placeItems: "center",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: 600,
  },
  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
    width: "100%",
    minWidth: 0,
    "& .fui-Field__control": {
      width: "100%",
      minWidth: 0,
    },
    "& .fui-Input": {
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
    },
  },
  input: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
});

const validateTenant = (tenant) => {
  const validationErrors = {};
  if (!tenant?.name?.trim()) validationErrors.name = ["validation.required"];
  return validationErrors;
};

function TenantEditor({ currentTenant, isOpen, setIsOpen, restoreFocusSourceAttributes }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { notify } = useNotify();
  const queryClient = useQueryClient();

  const [draftTenant, setDraftTenant] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [errors, setErrors] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef();

  const {
    mutateAsync: updateTenantAsync,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateCurrentTenant(["tenant", "current"], null, null, false);

  const {
    mutateAsync: updateTenantLogoAsync,
    isPending: isUploadingLogo,
    error: errorUploadingLogo,
    reset: resetUploadingLogo,
  } = useUpdateTenantLogo(["tenant", "current"], null, null, false);

  useEffect(() => {
    if (!isOpen || !currentTenant) return;
    setDraftTenant({ ...currentTenant });
    setValidationErrors(validateTenant(currentTenant));
    setPendingFile(null);
    setImageSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  }, [isOpen, currentTenant]);

  useEffect(() => {
    const newErrors = [];
    if (errorUpdating) {
      newErrors.push({
        message: errorUpdating?.message || t("generic.error"),
        type: "error",
        details: errorUpdating,
      });
    }
    if (errorUploadingLogo) {
      newErrors.push({
        message: errorUploadingLogo?.message || t("generic.error"),
        type: "error",
        details: errorUploadingLogo,
      });
    }

    if (newErrors.length > 0) {
      setErrors((prev) => [...prev, ...newErrors]);
    }
  }, [errorUpdating, errorUploadingLogo, t]);

  useEffect(() => {
    if (!pendingFile) return;
    const tempBlobUrl = URL.createObjectURL(pendingFile);
    setImageSrc(tempBlobUrl);
    return () => URL.revokeObjectURL(tempBlobUrl);
  }, [pendingFile]);

  const updateField = (field, value) => {
    const updated = { ...draftTenant, [field]: value };
    setDraftTenant(updated);
    setValidationErrors(validateTenant(updated));
  };

  const handleFileChange = (file) => {
    if (!file) return;
    setPendingFile(file);
  };

  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["fileBlobUrl", currentTenant?.logo_path] });
    setDraftTenant(currentTenant ? { ...currentTenant } : null);
    setValidationErrors({});
    setPendingFile(null);
    setImageSrc(null);
    setErrors([]);
    resetUpdating();
    resetUploadingLogo();
    if (fileInputRef.current) fileInputRef.current.value = null;
    setIsOpen(false);
  };

  const handleDrawHeaderTitleButtonOnClick = () => setIsOpen(false);

  const handleSave = async () => {
    const errorsMap = validateTenant(draftTenant);
    setValidationErrors(errorsMap);
    if (Object.keys(errorsMap).length > 0) return;

    try {
      const payload = { ...(draftTenant || {}) };
      delete payload.logo_path;
      delete payload.logo;

      const savedTenant = await updateTenantAsync(payload);

      if (pendingFile) {
        await updateTenantLogoAsync({ file: pendingFile });
      }

      notify(t("item.messages.saved", { name: savedTenant?.name || draftTenant?.name }), "success");
      setIsOpen(false);
      setPendingFile(null);
      setImageSrc(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    } catch {
      // Errors are surfaced via mutation error states and bars.
    }
  };

  const handleOpenChange = (_, data) => {
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const isBusy = isUpdating || isUploadingLogo;
  const drawerLogo = imageSrc;

  return (
    <div className={styles.root}>
      <Drawer
        {...restoreFocusSourceAttributes}
        type="overlay"
        position="end"
        separator
        size="medium"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={handleDrawHeaderTitleButtonOnClick}
              />
            }
          >
            <div className={styles.header}>
              {drawerLogo ? (
                <img src={drawerLogo} alt={t("generic.logo", "Logo")} className={styles.logo} />
              ) : (
                <Logo
                  logoUrl={currentTenant?.logo_ath || currentTenant?.logo_path || null}
                  alt={t("generic.logo", "Logo")}
                  className={styles.logo}
                  fallback={<div className={styles.placeholderLogo}>Logo</div>}
                />
              )}
              <div className={styles.headerText}>
                <span className={styles.title}>{draftTenant?.name || t("generic.tenant", "Tenant")}</span>
                <ImagePicker
                  label={t("generic.imageChange")}
                  onFileSelected={handleFileChange}
                  fileInputRef={fileInputRef}
                  accept="image/*"
                />
              </div>
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!draftTenant && isOpen && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody className={styles.body}>
          <div className={styles.content}>
            <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />

            <Field
              className={styles.field}
              label={t("generic.name")}
              validationState={validationErrors.name ? "error" : undefined}
              required
            >
              <Input
                className={styles.input}
                id="name"
                value={draftTenant?.name ?? ""}
                onChange={(e) => updateField("name", e.target.value)}
                maxLength={100}
              />
            </Field>

            <Field className={styles.field} label={t("generic.slug", "Slug")}>
              <Input
                className={styles.input}
                id="slug"
                value={draftTenant?.slug ?? ""}
                maxLength={50}
                readOnly
                disabled
              />
            </Field>

            <Field className={styles.field} label={t("generic.email")}>
              <Input
                className={styles.input}
                id="email"
                type="email"
                value={draftTenant?.email ?? ""}
                onChange={(e) => updateField("email", e.target.value)}
                maxLength={255}
              />
            </Field>

            <Field className={styles.field} label={t("generic.phone")}>
              <Input
                className={styles.input}
                id="phone"
                value={draftTenant?.phone ?? ""}
                onChange={(e) => updateField("phone", e.target.value)}
                maxLength={50}
              />
            </Field>

            {validationErrors && Object.keys(validationErrors).length > 0 && (
              <DataValidationMessageBar validationErrors={validationErrors} />
            )}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              appearance="primary"
              icon={isBusy ? <Spinner size="tiny" /> : undefined}
              onClick={async () => {
                const errors = validateTenant(draftTenant);
                if (Object.keys(errors).length > 0) {
                  setValidationErrors(errors);
                  return;
                }
                await handleSave();
              }}
              disabled={isBusy || !draftTenant || Object.keys(validationErrors).length > 0}
            >
              {t("generic.update")}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {t("generic.cancel")}
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}

export default TenantEditor;
